import { useState } from 'react'
import { Ticket, ChevronDown, ChevronUp } from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'

import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Ticket as TicketType } from '@/modules/Ticket/types/response'
import { useCustomerTickets } from './use-customer-tickets'

interface CustomerTicketsListProps {
    customerId: number
    startDate: string
    endDate: string
}

/**
 * Customer Tickets List Component
 * Displays tickets for a customer in a given date range
 * Features:
 * - Expandable/collapsible view (summary vs full table)
 * - Statistics summary (opening, in progress, pending, resolved)
 * - Pagination with URL params (tickets_ prefix)
 */
const CustomerTicketsList: React.FC<CustomerTicketsListProps> = ({
    customerId,
    startDate,
    endDate,
}) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const {
        tickets,
        total,
        stats,
        activeCount,
        isLoading,
        page,
        pageSize,
        columns,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useCustomerTickets({
        customerId,
        startDate,
        endDate,
    })

    // Loading state
    if (isLoading && tickets.length === 0) {
        return (
            <Paper className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Skeleton className="h-5 w-5" />
                    <Skeleton className="h-5 w-32" />
                </div>
                <Skeleton className="h-20 rounded-lg" />
            </Paper>
        )
    }

    // Empty state
    if (total === 0) {
        return (
            <Paper className="p-4">
                <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                        {t(
                            'no_tickets_in_period',
                            'Không có ticket trong khoảng thời gian này'
                        )}
                    </span>
                </div>
            </Paper>
        )
    }

    return (
        <Paper className="p-4">
            {/* Header with expand/collapse toggle */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Ticket className="w-4 h-4 text-gray-500" />
                    <Typogaphy className="font-medium text-gray-700 text-sm">
                        Tickets
                    </Typogaphy>
                    <span className="text-xs text-gray-400">({total})</span>
                    {activeCount > 0 && (
                        <span className="text-xs text-gray-600">
                            • {activeCount} đang xử lý
                        </span>
                    )}
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="h-7 text-xs text-gray-500"
                >
                    {isExpanded ? (
                        <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Thu gọn
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Chi tiết
                        </>
                    )}
                </Button>
            </div>

            {/* Content - Summary or Full Table */}
            {!isExpanded ? (
                // Collapsed view - Inline summary statistics
                <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Mới:</span>
                        <span className="font-semibold text-gray-700">
                            {stats.opening}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Đang xử lý:</span>
                        <span className="font-semibold text-gray-700">
                            {stats.inProgress}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Chờ:</span>
                        <span className="font-semibold text-gray-700">
                            {stats.pending}
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-gray-500">Đã xử lý:</span>
                        <span className="font-semibold text-green-600">
                            {stats.resolved + stats.closed}
                        </span>
                    </div>
                </div>
            ) : (
                // Expanded view - Full DataTable
                <DataTableCustomized<TicketType>
                    columns={columns}
                    rows={tickets}
                    size="small"
                    loading={isLoading}
                    emptyMessage={t('no_tickets', 'Không có ticket nào')}
                    rowKey={(row) => String(row.ticket_id)}
                    pagination={{
                        page,
                        pageSize,
                        total,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [5, 10, 20],
                    }}
                />
            )}
        </Paper>
    )
}

export default CustomerTicketsList
